import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { BannerAd } from './components/BannerAd';
import { FavoritesProvider } from './contexts/FavoritesContext';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import DishDetail from './pages/DishDetail';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import SupperClub from './pages/SupperClub';
import EventDetail from './pages/EventDetail';
import CategoryResults from './pages/CategoryResults';
import FoodFeed from './pages/FoodFeed';
import RestaurantProfile from './pages/RestaurantProfile';
import Premium from './pages/Premium';
import './index.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      
      // Check premium status if user is logged in
      if (currentUser) {
        // This would fetch from Firestore in production
        // For now, we'll check localStorage or a user document
        const premiumStatus = localStorage.getItem(`premium_${currentUser.uid}`) === 'true';
        setIsPremium(premiumStatus);
      } else {
        setIsPremium(false);
      }
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-1 h-20 bg-border mx-auto mb-8 animate-pulse"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <FavoritesProvider>
      <Router>
        <div className="App min-h-screen bg-background flex flex-col">
          <Header user={user} onSearchChange={setSearchQuery} />
          
          <main className="flex-1">
            <Routes>
              <Route 
                path="/" 
                element={
                  <Home 
                    user={user} 
                    searchQuery={searchQuery}
                  />
                } 
              />
              <Route 
                path="/login" 
                element={user ? <Navigate to="/" /> : <Login />} 
              />
              <Route 
                path="/signup" 
                element={user ? <Navigate to="/" /> : <Signup />} 
              />
              <Route 
                path="/dish/:id" 
                element={<DishDetail user={user} />} 
              />
              <Route 
                path="/leaderboard" 
                element={<Leaderboard user={user} />} 
              />
              <Route 
                path="/profile" 
                element={user ? <Profile user={user} /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/supperclub" 
                element={<SupperClub user={user} />} 
              />
              <Route 
                path="/supperclub/:id" 
                element={<EventDetail user={user} />} 
              />
              <Route 
                path="/best-of/:categoryId" 
                element={<CategoryResults user={user} />} 
              />
              <Route 
                path="/feed" 
                element={<FoodFeed user={user} />} 
              />
              <Route 
                path="/restaurant/:restaurantName" 
                element={<RestaurantProfile user={user} />} 
              />
              <Route 
                path="/premium" 
                element={<Premium user={user} />} 
              />
            </Routes>
          </main>
          
          {/* Banner Ad - Only shown for non-premium users */}
          {!isPremium && <BannerAd position="bottom" isPremiumUser={isPremium} />}
          
          <Footer />
        </div>
      </Router>
    </FavoritesProvider>
  );
}

export default App;
